// Saldo de la cuenta BindPSP. El saldo lo expone el CoreDBApi en
// GET /v1/Admin/bindpsp/balance (que a su vez consulta la API de BIND PSP).
// Requiere el JWT de CoreDB (`webadmin2.corejwt`, 1er factor) — el token
// post-MFA `webadmin2.jwt` lo firma webadmin2 y CoreDB no lo conoce.

const ADMIN_API =
  (import.meta.env.VITE_ADMIN_API as string | undefined) ??
  "https://api.cucuru.com/app";

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.corejwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

/**
 * Saldo ARS de la cuenta operativa BindPSP. Devuelve null si el banco no lo da.
 * `redirectOn401: false` (Dashboard, CP-468): un 401 solo tira error y no cierra
 * la sesión — en sandbox este endpoint da 401 siempre y no debe echar al usuario.
 */
export async function getBindBalance(opts: { redirectOn401?: boolean } = {}): Promise<number | null> {
  const res = await fetch(`${ADMIN_API}/v1/Admin/bindpsp/balance`, {
    headers: { ...authHeader(), Accept: "application/json" },
  });
  if (res.status === 401 && opts.redirectOn401 === false) {
    throw new Error("CoreDB rechazó la consulta del saldo (401).");
  }
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.corejwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
    throw new Error("Sesión expirada — volvé a iniciar sesión.");
  }
  if (!res.ok) throw new Error(`No se pudo obtener el saldo (HTTP ${res.status}).`);
  const data = await res.json().catch(() => null);
  if (typeof data === "number") return data;
  if (data && typeof data === "object" && typeof data.saldo === "number") return data.saldo;
  return null;
}

/**
 * CP-523: "Bind Status" de WA1. Interruptor `bind_status_prod` (ar_config) que
 * Portal Vendor 2 consulta cada 60 s: con 1 muestra el aviso de demoras a los
 * collectors AR#BIND. El GET es anónimo y CoreDB lo cachea 30 s.
 */
export async function getBindStatus(): Promise<0 | 1> {
  const res = await fetch(`${ADMIN_API}/v1/Admin/bind/status/prod`, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`No se pudo leer el estado (HTTP ${res.status}).`);
  const data = await res.json().catch(() => null);
  return Number(data?.status) === 1 ? 1 : 0;
}

/** Evento de ventana: la barra superior (Layout) actualiza el indicador sin esperar el próximo chequeo. */
export const BIND_STATUS_EVENT = "webadmin2:bind-status";

/** Igual que WA1. Exige admin de prd: un 401 solo es error, no cierra la sesión (como CP-488). */
export async function setBindStatus(value: 0 | 1): Promise<void> {
  const res = await fetch(`${ADMIN_API}/v1/Admin/bind/status/prod/${value}`, {
    method: "POST", headers: { ...authHeader(), Accept: "application/json" },
  });
  if (res.status === 401) throw new Error("CoreDB rechazó el cambio (401): solo un admin de prd puede cambiar Bind Status.");
  if (!res.ok) throw new Error(`No se pudo cambiar el estado (HTTP ${res.status}).`);
  window.dispatchEvent(new CustomEvent(BIND_STATUS_EVENT, { detail: value }));
}
